import {
  Typography,
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Chip,
} from '@mui/material';
import type { NurturingJourneyVersion } from 'app/pages/NurturingJourneySettingsComponent/types';
import { formatToDealerTime } from 'utils/dateUtils';

interface Segment {
  segment_id: string;
  segment_name: string;
  override?: boolean;
  [key: string]: any;
}

interface NurturingJourneyControlsProps {
  segments: Segment[];
  selectedSegmentIndex: number;
  recordType: 'unsold' | 'sold';
  onSegmentChange: (index: number) => void;
  onRecordTypeChange: (type: 'unsold' | 'sold') => void;
  labelPrefix?: string;
  versions?: NurturingJourneyVersion[];
  selectedVersionId?: string;
  versionsLoading?: boolean;
  onVersionChange?: (id: string) => void;
  onOverrideChange?: (value: boolean) => void;
}

function getVersionDisplayInfo(v: NurturingJourneyVersion): {
  versionLabel: string;
  status: string;
  dateStr: string;
} {
  const status = v.nurturing_journey_status;
  const versionLabel = v.nurturing_journey_version != null ? `v${v.nurturing_journey_version}` : 'Draft';

  if (status === 'PUBLISHED' && v.published_date) {
    return { versionLabel, status: 'PUBLISHED', dateStr: formatToDealerTime(v.published_date) };
  }
  if (status === 'ARCHIVED' && v.archived_date) {
    return { versionLabel, status: 'ARCHIVED', dateStr: formatToDealerTime(v.archived_date) };
  }
  if (status === 'CREATED' && v.created_at) {
    return { versionLabel, status: 'CREATED', dateStr: formatToDealerTime(v.created_at) };
  }
  return { versionLabel, status, dateStr: '' };
}

const STATUS_CHIP_COLORS: Record<string, 'success' | 'default' | 'info'> = {
  PUBLISHED: 'success',
  ARCHIVED: 'default',
  CREATED: 'info',
};

function VersionOptionContent({ v }: { v: NurturingJourneyVersion }) {
  const { versionLabel, status, dateStr } = getVersionDisplayInfo(v);
  const chipColor = STATUS_CHIP_COLORS[status] ?? 'default';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 0.25 }}>
      <Typography variant="body2" fontWeight={600}>
        {versionLabel}
      </Typography>
      <Chip
        label={status}
        color={chipColor}
        size="small"
        sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
      />
      {dateStr && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
          {dateStr}
        </Typography>
      )}
    </Box>
  );
}

export const NurturingJourneyControls = ({
  segments,
  selectedSegmentIndex,
  recordType,
  onSegmentChange,
  onRecordTypeChange,
  labelPrefix = 'nurturing',
  versions,
  selectedVersionId,
  versionsLoading,
  onVersionChange,
  onOverrideChange,
}: NurturingJourneyControlsProps) => {
  const showVersions =
    onVersionChange != null && versions != null && versions.length > 0;
  const selectedSegment = segments[selectedSegmentIndex];
  const isAllSegment = selectedSegment?.segment_id === 'ALL';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2,
        mb: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {(showVersions || versionsLoading) && (
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel id={`${labelPrefix}-version-select-label`}>Version</InputLabel>
            <Select
              labelId={`${labelPrefix}-version-select-label`}
              label="Version"
              value={selectedVersionId ?? ''}
              onChange={e => onVersionChange?.(e.target.value as string)}
              disabled={versionsLoading}
              endAdornment={
                versionsLoading ? (
                  <CircularProgress size={16} sx={{ mr: 2 }} />
                ) : undefined
              }
              renderValue={value => {
                const v = (versions ?? []).find(x => x._id === value);
                return v ? <VersionOptionContent v={v} /> : value;
              }}
            >
              {(versions ?? []).map(v => (
                <MenuItem key={v._id} value={v._id}>
                  <VersionOptionContent v={v} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          View/Edit config for
        </Typography>
        <ToggleButtonGroup
          value={recordType}
          exclusive
          onChange={(_, v) => v != null && onRecordTypeChange(v)}
          size="small"
          sx={{
            height: 40,
            width: 200,
            display: 'flex',
            '& .MuiToggleButton-root': { flex: 1, minWidth: 0 },
          }}
        >
          <ToggleButton value="unsold" aria-label="Unsold config">
            Unsold
          </ToggleButton>
          <ToggleButton value="sold" aria-label="Sold config">
            Sold
          </ToggleButton>
        </ToggleButtonGroup>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id={`${labelPrefix}-segment-select-label`}>Segment</InputLabel>
          <Select
            labelId={`${labelPrefix}-segment-select-label`}
            label="Segment"
            value={selectedSegmentIndex}
            onChange={e => onSegmentChange(Number(e.target.value))}
          >
            {segments.map((seg, idx) => (
              <MenuItem key={seg.segment_id} value={idx}>
                {seg.segment_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(selectedSegment?.override)}
              onChange={(_, checked) => onOverrideChange?.(checked)}
              disabled={isAllSegment}
              color="primary"
            />
          }
          label="Override"
          sx={{ ml: 0.5 }}
        />
      </Box>
    </Box>
  );
};
